import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function BankDetailsModal({ isOpen, onClose, website, onSuccess }) {
  const [formData, setFormData] = useState({
    bankName: '',
    accountName: '',
    accountNumber: ''
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (website && isOpen) {
      setFormData({
        bankName: website.paymentSettings?.bankName || '',
        accountName: website.paymentSettings?.accountName || '',
        accountNumber: website.paymentSettings?.accountNumber || ''
      })
    }
  }, [website, isOpen])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.bankName.trim()) {
      toast.error('Bank name is required')
      return
    }
    if (!formData.accountName.trim()) {
      toast.error('Account name is required')
      return
    }
    if (!formData.accountNumber.trim()) {
      toast.error('Account number is required')
      return
    }

    setSaving(true)

    try {
      const token = localStorage.getItem('userAdminToken') || localStorage.getItem('token') || localStorage.getItem('globalAdminToken')
      if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      await axios.patch(`/websites/${website._id}/payment-settings`, {
        bankName: formData.bankName.trim(),
        accountName: formData.accountName.trim(),
        accountNumber: formData.accountNumber.trim()
      })

      toast.success('Bank details updated successfully')
      onSuccess && onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating bank details:', error)
      toast.error(error.response?.data?.message || 'Failed to update bank details')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Bank Details Configuration</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Bank Details</h3>
            <p className="text-sm text-gray-600">Users will pay via bank transfer using these details.</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bank Name *
              </label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Access Bank"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Name *
              </label>
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Number *
              </label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 1234567890"
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  Save Details
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}